import { Platform, Linking, Alert, NativeModules } from 'react-native';
import i18n from '@/i18n';
import BackgroundGeolocation from 'react-native-background-geolocation';

/**
 * Utility for detecting and requesting exemption from OEM battery
 * optimisation (Samsung, Xiaomi, Huawei, etc.).
 *
 * On Android 6+ the standard `ACTION_REQUEST_IGNORE_BATTERY_OPTIMIZATIONS`
 * intent can whitelist the app from Doze & App Standby.  On many OEMs
 * (especially Samsung) there are *additional* manufacturer-specific settings
 * that the standard intent does NOT cover.  This module:
 *
 * 1. Checks `isIgnoringBatteryOptimizations` via a lightweight native module.
 * 2. If not whitelisted, prompts the user with a system dialog.
 * 3. For known OEMs, shows an **additional** in-app alert guiding the user
 *    to the manufacturer-specific settings page.
 */

// ─── Native module access ────────────────────────────────────────────

let _powerModule: any = null;
function getPowerModule(): any {
  if (_powerModule !== undefined) return _powerModule;
  try {
    // Try our custom Expo module first
    const { requireNativeModule } = require('expo-modules-core');
    _powerModule = requireNativeModule('BatteryOptimization');
  } catch {
    // Custom module not available — fall back to pure JS approach
    _powerModule = null;
  }
  return _powerModule;
}

// ─── OEM detection ───────────────────────────────────────────────────

export type OEMName = 'samsung' | 'xiaomi' | 'huawei' | 'oppo' | 'vivo' | 'oneplus' | 'asus' | 'none';

/**
 * Detect the device manufacturer using Build.MANUFACTURER / Build.BRAND.
 */
export async function detectOEM(): Promise<OEMName> {
  if (Platform.OS !== 'android') return 'none';

  try {
    const mod = getPowerModule();
    if (mod?.getManufacturer && mod?.getBrand) {
      const manufacturer = (await mod.getManufacturer()) || '';
      const brand = (await mod.getBrand()) || '';
      return matchOEM(manufacturer.toLowerCase(), brand.toLowerCase());
    }
  } catch {
    // fall through to heuristics
  }

  // Fallback: use DeviceInfo from expo or React Native
  try {
    const DeviceInfo = NativeModules.DeviceInfo || NativeModules.RNDeviceInfo;
    if (DeviceInfo) {
      const manufacturer = ((DeviceInfo.manufacturer as string) || '').toLowerCase();
      const brand = ((DeviceInfo.brand as string) || '').toLowerCase();
      return matchOEM(manufacturer, brand);
    }
  } catch {
    // ignore
  }

  return 'none';
}

function matchOEM(manufacturer: string, brand: string): OEMName {
  const combined = `${manufacturer} ${brand}`;
  if (combined.includes('samsung')) return 'samsung';
  if (combined.includes('xiaomi') || combined.includes('redmi') || combined.includes('poco')) return 'xiaomi';
  if (combined.includes('huawei') || combined.includes('honor')) return 'huawei';
  if (combined.includes('oppo') || combined.includes('realme')) return 'oppo';
  if (combined.includes('vivo')) return 'vivo';
  if (combined.includes('oneplus')) return 'oneplus';
  if (combined.includes('asus')) return 'asus';
  return 'none';
}

// ─── Battery optimization check ──────────────────────────────────────

export interface BatteryCheckResult {
  isOptimized: boolean;    // true if battery optimisation is restricting the app
  oem: OEMName;            // detected OEM manufacturer
  needsOEMConfig: boolean;  // true if OEM-specific settings are likely needed
}

/**
 * Race a promise against a timeout. OEM native modules have been observed to
 * never resolve on Samsung One UI firmware (Android 17 / S25), silently
 * dead-ending any code that awaits them — never trust them to settle.
 */
async function withTimeout(promise: Promise<unknown> | null | undefined, ms: number): Promise<any> {
  if (!promise) return null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), ms);
  });
  try {
    return (await Promise.race([promise, timeout])) ?? null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer!);
  }
}

/**
 * Full battery-optimisation check.  Returns an object describing whether
 * the app is currently restricted and what OEM it's running on.
 *
 * FAILS CLOSED: if the native check errors, times out or is unavailable on an
 * aggressive OEM, we report "optimized" = true so the guidance prompt still
 * shows. Silently assuming "fine" on Samsung is what left users ungated on
 * One UI 9. Also cross-checks the Transistor SDK's own
 * deviceSettings.isIgnoringBatteryOptimizations()
 */
export async function checkBatteryOptimizationStatus(): Promise<BatteryCheckResult> {
  if (Platform.OS !== 'android') {
    return { isOptimized: false, oem: 'none', needsOEMConfig: false };
  }

  const oem = await detectOEM();
  const aggressiveOem = oem !== 'none';

  // 1) Custom module (Expo native module)
  let nativeResult: boolean | null = null;
  try {
    const mod = getPowerModule();
    if (mod?.isIgnoringBatteryOptimizations) {
      nativeResult = await withTimeout(mod.isIgnoringBatteryOptimizations(), 3000);
    }
  } catch {
    nativeResult = null;
  }

  // 2) SDK's own check as a cross-check / fallback
  let sdkResult: boolean | null = null;
  try {
    const ds: any = (BackgroundGeolocation as any).deviceSettings;
    if (ds?.isIgnoringBatteryOptimizations) {
      sdkResult = await withTimeout(ds.isIgnoringBatteryOptimizations(), 3000);
    }
  } catch {
    sdkResult = null;
  }

  // Both results mean "exempt from battery optimization"; null = unknown.
  // If either says NOT exempt, or both are unknown, assume restricted —
  // better an extra prompt than dead tracking on a throttled device.
  const isIgnored = nativeResult === true && (sdkResult === true || sdkResult === null);

  return {
    isOptimized: !isIgnored,
    oem,
    needsOEMConfig: aggressiveOem && !isIgnored,
  };
}

// ─── Request battery optimisation exemption ──────────────────────────

/**
 * Opens the system dialog asking the user to whitelist this app from
 * battery optimisation. Uses the standard Android intent.
 */
export async function requestBatteryOptimizationExemption(): Promise<boolean> {
  if (Platform.OS !== 'android') return true;

  try {
    const mod = getPowerModule();
    if (mod?.requestIgnoreBatteryOptimizations) {
      // OEM firmware has been observed to never resolve these calls — race a
      // timeout and fall through to the URL-intent fallback if it hangs.
      const shown = await withTimeout(mod.requestIgnoreBatteryOptimizations(), 3000);
      if (shown !== null) return shown;
    }
  } catch {
    // fall through
  }

  // Fallback: use Linking to open the system intent
  try {
    const packageName = 'com.tailtrailsclub.app';
    const url = `android.settings.REQUEST_IGNORE_BATTERY_OPTIMIZATIONS?package=${packageName}`;
    const supported = await Linking.canOpenURL(url);
    if (supported) {
      await Linking.openURL(url);
      return true;
    }
  } catch {
    // fall through
  }

  // Final fallback: open app settings
  try {
    await Linking.openSettings();
    return true;
  } catch {
    return false;
  }
}

/**
 * Open the Android battery optimisation settings page.
 * Tries OEM-specific settings first.
 */
export async function openBatterySettings(): Promise<boolean> {
  if (Platform.OS !== 'android') return true;

  // Try the Transistor SDK's DeviceSettings first — it resolves OEM-specific
  // battery screens (e.g. Samsung's power manager) that raw intents miss.
  try {
    const ds: any = (BackgroundGeolocation as any).deviceSettings;
    const request = await withTimeout(ds?.showPowerManager(), 3000);
    if (request?.action) {
      await ds.show(request);
      return true;
    }
  } catch {
    // fall through
  }

  try {
    const mod = getPowerModule();
    if (mod?.openBatterySettings) {
      const opened = await withTimeout(mod.openBatterySettings(), 3000);
      if (opened !== null) return opened;
    }
  } catch {
    // fall through
  }

  // Fallback: try standard battery settings intents
  const oem = await detectOEM();
  const settingsIntents = [
    'android.settings.IGNORE_BATTERY_OPTIMIZATION_SETTINGS',
    'android.settings.BATTERY_OPTIMIZATION_SETTINGS',
  ];

  for (const intent of settingsIntents) {
    try {
      const supported = await Linking.canOpenURL(intent);
      if (supported) {
        await Linking.openURL(intent);
        return true;
      }
    } catch {
      continue;
    }
  }

  // Final fallback: open app settings
  try {
    await Linking.openSettings();
    return true;
  } catch {
    return false;
  }
}

// ─── High-level convenience function ──────────────────────────────────

/**
 * Show an alert guiding the user through battery optimisation settings.
 * This is the main entry point for the UI flow.
 *
 * Returns true if the user opened at least one settings page.
 */
export function showBatteryOptimizationAlert(result: BatteryCheckResult): Promise<boolean> {
  return new Promise((resolve) => {
    const oemName = getOEMDisplayName(result.oem, i18n);
    const hasOEMIssues = result.oem !== 'none';

    const title = hasOEMIssues
      ? i18n.t('tracking.oemAlertTitle', { oem: oemName })
      : i18n.t('tracking.batteryAlertTitle');

    const message = hasOEMIssues
      ? i18n.t('tracking.oemAlertMessage', { oem: oemName })
      : i18n.t('tracking.batteryAlertMessage');

    const buttons: any[] = [
      { text: i18n.t('common.skip'), style: 'cancel' as const, onPress: () => resolve(false) },
    ];

    if (result.isOptimized) {
      buttons.push({
        text: i18n.t('tracking.requestExemption'),
        onPress: async () => {
          await requestBatteryOptimizationExemption();
          resolve(true);
        },
      });
    }

    if (hasOEMIssues) {
      buttons.push({
        text: i18n.t('tracking.openOemSettings'),
        onPress: async () => {
          await openBatterySettings();
          resolve(true);
        },
      });
    }

    Alert.alert(title, message, buttons, { cancelable: true });
  });
}

function getOEMDisplayName(oem: OEMName, i18nInstance?: typeof i18n): string {
  const t = i18nInstance || i18n;
  switch (oem) {
    case 'samsung': return t.t('tracking.samsung');
    case 'xiaomi': return t.t('tracking.xiaomi');
    case 'huawei': return t.t('tracking.huawei');
    case 'oppo': return t.t('tracking.oppo');
    case 'vivo': return t.t('tracking.vivo');
    case 'oneplus': return t.t('tracking.oneplus');
    case 'asus': return t.t('tracking.asus');
    default: return t.t('tracking.yourDevice');
  }
}